import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { PageFooter } from "../../app/PageFooter";
import { PATHS } from "../../app/paths";
import { TopBar } from "../../app/TopBar";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { errorMessage } from "../cockpit/format";
import { createGroup, deleteGroup, listGroups, renameGroup, sortGroups } from "./groupsApi";
import type { PolymerGroupView } from "./types";

const ICON_BUTTON =
  "inline-flex items-center justify-center bg-transparent border border-rs-jet-light rounded p-1 text-rs-light-gray disabled:opacity-40 disabled:cursor-not-allowed";

function usage(count: number): string {
  if (count === 0) return "Not used yet";
  return `${count.toLocaleString()} ${count === 1 ? "facility" : "facilities"}`;
}

export function PolymerGroupsPage() {
  const navigate = useNavigate();
  const { appRole } = useAppUser();
  const canManage = appRole === "Uploader" || appRole === "Admin";
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [groups, setGroups] = useState<PolymerGroupView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await listGroups(apiFetchRef.current);
        if (!cancelled) setGroups(loaded);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const add = () =>
    run(async () => {
      const made = await createGroup(apiFetchRef.current, newName.trim());
      setGroups((current) => sortGroups([...(current ?? []), made]));
      setNewName("");
    });

  const save = () =>
    run(async () => {
      if (!editing) return;
      const renamed = await renameGroup(apiFetchRef.current, editing.id, editing.name.trim());
      setGroups((current) =>
        sortGroups((current ?? []).map((g) => (g.polymerGroupId === renamed.polymerGroupId ? renamed : g))),
      );
      setEditing(null);
    });

  const remove = (group: PolymerGroupView) =>
    run(async () => {
      await deleteGroup(apiFetchRef.current, group.polymerGroupId, group.facilityCount > 0);
      setGroups((current) => (current ?? []).filter((g) => g.polymerGroupId !== group.polymerGroupId));
      setConfirming(null);
    });

  return (
    <>
      <TopBar title="Polymer Groups">
        <Btn variant="ghost" onClick={() => navigate(PATHS.plantRegistry)}>
          <span className="material-symbols-sharp">arrow_back</span>Plant Registry
        </Btn>
      </TopBar>

      <main className="pt-[26px] px-7 pb-[70px]">
        <Card className="max-w-3xl">
          <p className="mt-0 mb-4 text-[13px] text-rs-light-gray">
            These are the groups chosen when a plant is verified, and they fill the Groups column of the export.
            Renaming a group changes its name on every plant that uses it.
          </p>

          {error && <p className="text-rs-red text-[13px] mt-0 mb-3">{error}</p>}

          {canManage && (
            <form
              className="flex items-end gap-2 mb-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (newName.trim()) void add();
              }}
            >
              <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray flex-1">
                Add a group
                <Input
                  value={newName}
                  maxLength={120}
                  placeholder="e.g. Ethylene Oxide (EO)"
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <Btn type="submit" disabled={busy || !newName.trim()}>
                <span className="material-symbols-sharp">add</span>Add
              </Btn>
            </form>
          )}

          <div className="card p-0 overflow-auto">
            <table>
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Used by</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {!groups && !error && (
                  <tr>
                    <td colSpan={canManage ? 3 : 2}>Loading…</td>
                  </tr>
                )}
                {groups?.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 3 : 2}>No groups yet.</td>
                  </tr>
                )}
                {groups?.map((group) => (
                  <tr key={group.polymerGroupId}>
                    <td>
                      {editing?.id === group.polymerGroupId ? (
                        <Input
                          value={editing.name}
                          maxLength={120}
                          aria-label={`Name for ${group.name}`}
                          autoFocus
                          onChange={(e) => setEditing({ id: group.polymerGroupId, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && editing.name.trim()) void save();
                            if (e.key === "Escape") setEditing(null);
                          }}
                        />
                      ) : (
                        group.name
                      )}
                    </td>
                    <td className="text-rs-light-gray">{usage(group.facilityCount)}</td>
                    {canManage && (
                      <td>
                        {editing?.id === group.polymerGroupId ? (
                          <div className="flex gap-2">
                            <Btn disabled={busy || !editing.name.trim()} onClick={() => void save()}>
                              Save
                            </Btn>
                            <Btn variant="ghost" onClick={() => setEditing(null)}>
                              Cancel
                            </Btn>
                          </div>
                        ) : confirming === group.polymerGroupId ? (
                          <div className="flex flex-wrap items-center gap-2 text-[13px]">
                            <span>
                              {group.facilityCount > 0
                                ? `Used by ${group.facilityCount.toLocaleString()} ${
                                    group.facilityCount === 1 ? "facility" : "facilities"
                                  }. Deleting removes it from them.`
                                : "Delete this group?"}
                            </span>
                            <Btn variant="danger" disabled={busy} onClick={() => void remove(group)}>
                              Delete
                            </Btn>
                            <Btn variant="ghost" onClick={() => setConfirming(null)}>
                              Cancel
                            </Btn>
                          </div>
                        ) : (
                          <div className="flex gap-1.5">
                            <button
                              className={`${ICON_BUTTON} hover:text-rs-green hover:border-rs-green`}
                              aria-label={`Rename ${group.name}`}
                              title="Rename"
                              disabled={busy}
                              onClick={() => {
                                setConfirming(null);
                                setEditing({ id: group.polymerGroupId, name: group.name });
                              }}
                            >
                              <span className="material-symbols-sharp text-[18px]">edit</span>
                            </button>
                            <button
                              className={`${ICON_BUTTON} hover:text-rs-red hover:border-rs-red`}
                              aria-label={`Delete ${group.name}`}
                              title="Delete"
                              disabled={busy}
                              onClick={() => {
                                setEditing(null);
                                setConfirming(group.polymerGroupId);
                              }}
                            >
                              <span className="material-symbols-sharp text-[18px]">delete</span>
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
      <PageFooter />
    </>
  );
}
